---
title: "Scaling In/Out, Trailing Stops and Partial Take-Profit"
slug: scaling-trailing-stops-partial-take-profit
shortTitle: "Scaling & Trailing Stops"
description: "How to build and reduce positions in pieces, bank partial profits and trail a stop by hand on {{BRAND}}, with worked USDT examples and the trade-offs of each technique."
level: advanced
category: trading
course: advanced
order: 9
topics: [risk-management, trading-strategies]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/scaling-trailing-stops-partial-take-profit.svg
faqs:
  - q: "Does {{BRAND}} have an automatic trailing stop?"
    a: "Not at the moment. You can trail a stop by hand: open Edit TP / SL on the position and enter a new SL Price, or drag the stop-loss line on the desktop chart and confirm."
  - q: "How do I close only part of a position?"
    a: "On desktop, Instant Trade has 25% and 50% close buttons that close that share of your open positions on the market. The regular Close button in Open Positions closes the whole position. You can also open a trade in several smaller pieces and close them separately."
  - q: "Is adding to a winning position risky?"
    a: "It increases your position size, so a reversal costs more. Traders who add to winners usually move the stop on the earlier piece first, so the combined risk stays within their plan."
  - q: "Why not just take the whole profit at the first target?"
    a: "You can. Partial take-profit is a trade-off: it banks some gain early and smooths results, but your best trades make less than they would if you had held the full size."
  - q: "How tight should a trailing stop be?"
    a: "Tight enough to protect a meaningful part of the gain, loose enough that normal swings do not close the trade. Many traders base the distance on recent volatility or recent swing lows rather than a round number."
---

Most beginners treat a trade as one decision: open it, then close it. More experienced traders often manage a position in stages. They **scale in**, building the position in pieces as the trade proves itself. They **scale out**, banking part of the profit at a first target. And they **trail** the stop loss behind the price, so a winning trade gives back less if it turns.

None of these techniques improves a bad entry, and each has a cost. This lesson explains the math behind each one with worked USDT examples, and shows how to carry them out on {{BRAND}}, where trailing is done by hand and partial closes happen through Instant Trade or by opening a trade in pieces.

<CTA />

<KeyTakeaways>

- **Scale in only while risk stays inside the plan.** Adding to a winner after moving the earlier stop can keep total risk flat; adding to a loser raises it.
- **Partial take-profit is a trade-off.** It banks gains and smooths results, at the price of smaller wins on your best trades.
- **Trailing stops lock in gains, if the distance fits the market.** Too tight and noise closes the trade; too loose and you give back most of the move.
- **On {{BRAND}} you manage this by hand.** Trail with Edit TP / SL or by dragging the stop line, and close part of a position with Instant Trade's 25% or 50% buttons on desktop.

</KeyTakeaways>

## Scaling in: building a position in pieces

**Scaling in** means entering a position in several orders instead of one. There are two very different versions:

- **Adding to a winner (pyramiding).** You add only after the price has moved your way, usually after moving the first stop up.
- **Adding to a loser (averaging down).** You add as the price moves against you, lowering your average entry but increasing your size while the trade is going wrong.

<Example title="Adding to a winning ETH/USDT trade (illustrative prices)">

You buy 1 ETH at 3,000 USDT with a stop at 2,940: risk = 60 × 1 = **60 USDT**.

The price rises to 3,090. You move the first stop to 3,030, which now locks in (3,030 − 3,000) × 1 = **+30 USDT**. You then buy 0.5 ETH at 3,090 with its stop also at 3,030: risk = 60 × 0.5 = **30 USDT**.

- Average entry = (1 × 3,000 + 0.5 × 3,090) ÷ 1.5 = 4,545 ÷ 1.5 = **3,030**.
- If both stops are hit: +30 − 30 = **0 USDT** before costs. You hold a bigger position with no more risk than at the start.

</Example>

On {{BRAND}}, each order you place opens its own position, with its own row in **Open Positions**, its own entry price and its own TP / SL. That makes each piece easy to manage separately.

{/* TODO: verify with platform — that each new order on the same market opens a separate row in Open Positions. */}

<Warning title="Averaging down can turn a small loss into a large one">

Say you buy 1 ETH at 3,000, it falls to 2,900, and you buy another 1 ETH. Your average entry is 2,950, but every further 100 USDT drop now costs 200 USDT instead of 100. Under cross margin, that growing loss draws on your whole account. If you ever add to a losing trade, decide the maximum size and the final stop before the first order, and never move the stop further away.

</Warning>

## Scaling out and partial take-profit

**Scaling out** means closing a position in pieces. The most common version is a **partial take-profit**: close part of the trade at a first target, then move the stop on the rest to your entry price (**breakeven**), so the remainder can run without risking the original amount.

<Example title="Half off at 1R on BTC/USDT (illustrative prices)">

You buy 0.1 BTC at 60,000 with a stop at 59,000: risk = 1,000 × 0.1 = **100 USDT** (1R). At 61,000 you close half, banking 1,000 × 0.05 = **+50 USDT**, and move the stop on the remaining 0.05 BTC to 60,000.

</Example>

<CompareTable caption="Scaling out versus holding the full size, same trade (before costs)">

| What the price does next | Half off at 61,000 | Full size, stop moved to 60,000 |
| --- | --- | --- |
| Falls to the stop before 61,000 | −100 USDT | −100 USDT |
| Reaches 61,000, then returns to 60,000 | +50 USDT | 0 USDT |
| Runs to 63,000 and you exit there | +50 + (3,000 × 0.05) = +200 USDT | 3,000 × 0.1 = +300 USDT |

</CompareTable>

Neither column is better in every case. Scaling out produces steadier results and fewer winners that turn into scratches; holding the full size earns more on the big runs. Which suits you depends on your strategy's results, which is why it pays to test both.

### How to close part of a position on {{BRAND}}

The **Close** button in **Open Positions** closes the whole position. To close part of it, you have two options:

1. **Instant Trade (desktop).** Once set up, its Close row has **25%**, **50%** and **100%** buttons. Each one closes that share of *every* open position you hold on that market, at market, in one click with no confirmation step. If you hold several pieces, or a buy and a sell, all of them shrink together.
2. **Open in pieces.** Place two orders instead of one, for example two buys of 0.05 BTC, the first with a TP Price of 61,000 and the second with a TP Price of 63,000, both with an SL Price of 59,000. When the first take profit triggers, it closes on its own and shows **Take profit** in the Reason column of **Closed Positions**. Then move the second piece's stop to breakeven.

{/* TODO: verify with platform — Instant Trade close buttons (desktop only), that the part left open keeps its TP / SL, and minimum volume rules for partial closes. */}

## Trailing stops

A **trailing stop** follows the price as it moves in your favor and never moves back. Traders set the distance in several ways:

- **Fixed distance:** a set number of USDT behind the best price or close.
- **Percentage:** a set percent behind it.
- **Volatility-based:** a multiple of the average true range, so the stop widens in wild markets. [Volume and volatility](/academy/article/volume-and-volatility) explains ATR.
- **Structure-based:** just beyond each new swing low (for a buy) or swing high (for a sell).

{{BRAND}} does not offer an automatic trailing stop order, so you trail by hand. That has one advantage: you can follow a rule based on closing prices, which ignores brief spikes. [Setting SL/TP and trailing stops on {{BRAND}}](/academy/article/setting-tp-sl-trailing-stops) walks through the screens in more detail.

{/* TODO: verify with platform — automatic trailing stops are not currently offered. */}

<Example title="Trailing a gold trade by hand (illustrative prices)">

You buy 0.05 lots of GOLD/USDT (5 oz) at 2,400 with a stop at 2,380: risk = 20 × 5 = **100 USDT**. Your rule: after each 4h candle closes, move the stop to 20 below the highest close so far, never lower.

- A close at 2,425 moves the stop to 2,405, locking (2,405 − 2,400) × 5 = **+25 USDT**.
- A close at 2,450 moves it to 2,430, locking 30 × 5 = **+150 USDT**.
- A close at 2,440 changes nothing, because the stop never moves down.
- A pullback to 2,430 closes the trade for about **+150 USDT** before costs, or less if the stop fills below its level in a fast market.

</Example>

<Steps>
<Step title="Open the position's stop">

In **Open Positions**, choose **Edit TP / SL** on the row. On desktop you can instead drag the stop-loss line on the chart and confirm.

</Step>
<Step title="Enter the new stop">

Type the new **SL Price**. For a buy, it must sit below the current Bid; for a sell, above the current Ask.

</Step>
<Step title="Save and check">

Confirm, then check the TP / SL column shows the new level. Note the change in your journal.

</Step>
<Step title="Let alerts do the watching">

In **Account → Alerts**, a **Price rises above** alert at the level where your rule says to move the stop next means you do not have to watch every candle.

</Step>
</Steps>

<ProTip title="Write the rule before you enter">

Decide your scaling and trailing rules before the trade opens: where you add, where you take part off, and how the stop moves. Then you only follow a rule, rather than making new decisions while a position is moving fast.

</ProTip>

## Conclusion

Scaling in, scaling out and trailing stops let you manage a trade in stages rather than in one all-or-nothing decision. Each comes with a trade-off: pyramiding increases size, partial profits trim your best trades, and trailing stops can be shaken out by normal swings. On {{BRAND}}, you trail by moving your stop with Edit TP / SL or the chart line, and you take partial profits through Instant Trade or by opening in pieces. Pair these with a solid [stop loss and take profit](/academy/article/stop-loss-take-profit) habit and test whichever rules you choose before you rely on them.
